import { isAxiosError } from "axios";

import { CheckoutError } from "@lib/api/checkout.types";
// Imported from the submodule (not the "@lib/authentication" barrel) so tests
// that mock the barrel don't shadow the helper.
import { isAuthError } from "@lib/authentication/amplify/amplify.helpers";

// Classification of a PATCH /customers/me/email rejection, shared by the
// checkout post-charge email correction (VariantSetPassword) and the member
// menu's Change Email panel. Each caller owns its own user copy — the two
// contexts need different session-expiry escapes (reset link vs. log in
// again) — so this module classifies, it never translates.
export type UpdateEmailErrorKind =
  | { kind: "session" }
  | { kind: "network" }
  | { kind: "emailInUse" }
  | { kind: "invalidEmail" }
  | { kind: "backendMessage"; message: string }
  | { kind: "unknown" };

// A session failure can't be fixed by retrying — it surfaces two ways: an
// HTTP 401 (stale token rejected server-side) or a NON-axios rejection from
// the pre-flight — updateCustomerEmail calls Auth.currentAuthenticatedUser()
// BEFORE the PATCH, and parseAuthResult re-throws the mapped auth-error
// object ({ code, message, name }) with no HTTP response at all. Matched by
// shape via isAuthError so an unrelated non-axios throw (e.g. a TypeError)
// falls through to "unknown" instead of the session dead end.
export const isUpdateEmailSessionFailure = (e: unknown): boolean =>
  isAxiosError(e) ? e.response?.status === 401 : isAuthError(e);

export const classifyUpdateEmailError = (e: unknown): UpdateEmailErrorKind => {
  if (isUpdateEmailSessionFailure(e)) {
    return { kind: "session" };
  }
  if (isAxiosError(e)) {
    if (!e.response) {
      // Request never completed: timeout (the 15s axios cap — also what the
      // endpoint's slow-down rate limiter manufactures under rapid retries)
      // or a network drop. Recoverable by retrying.
      return { kind: "network" };
    }
    const name = e.response.data?.name;
    if (name === CheckoutError.CUSTOMER_EXISTS) {
      return { kind: "emailInUse" };
    }
    if (name === CheckoutError.INVALID_INPUT) {
      return { kind: "invalidEmail" };
    }
    // An unmapped 4xx that carries purpose-written backend copy: surface it
    // rather than discarding it for the generic fallback. 4xx ONLY — the
    // backend's default 500 handler echoes raw internal err.message, which
    // must never reach a user.
    if (e.response.status >= 400 && e.response.status < 500) {
      const backendMessage = e.response.data?.message;
      if (typeof backendMessage === "string" && backendMessage.length > 0) {
        return { kind: "backendMessage", message: backendMessage };
      }
    }
  }
  // 5xx, unmapped 4xx without copy, or an unknown non-axios throw.
  return { kind: "unknown" };
};
